import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { formatSize, importsApi, type ImportProfile, type ImportVersion, type PartnerRow } from '@/lib/imports';

interface Props {
  onStarted: (v: ImportVersion) => void;
  onOpenExisting: (v: ImportVersion) => void;
}

const MAX_BYTES = 40 * 1024 * 1024;

export default function StepUpload({ onStarted, onOpenExisting }: Props) {
  const { t, i18n } = useTranslation();
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [partner, setPartner] = useState('');
  const [profiles, setProfiles] = useState<ImportProfile[]>([]);
  const [profileId, setProfileId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [notifyPartner, setNotifyPartner] = useState(true);
  const [recent, setRecent] = useState<ImportVersion[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-GB';

  useEffect(() => {
    importsApi
      .partners()
      .then((rows) => {
        setPartners(rows);
        const preset = new URLSearchParams(window.location.search).get('partner');  // переход из онбординга партнёра
        if (!partner && rows.length) setPartner(preset && rows.some((r) => r.id === preset) ? preset : rows[0].id);
      })
      .catch((e: ApiError) => setError(e.code));
    importsApi.list().then(setRecent).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!partner) return;
    importsApi
      .profiles(partner)
      .then((p) => {
        setProfiles(p);
        setProfileId(p[0]?.id ?? '');
      })
      .catch(() => setProfiles([]));
  }, [partner]);

  const formats = ['PDF', 'XLSX', 'CSV', 'JPG', 'PNG'];
  const pick = (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setError('file_too_large');
      return;
    }
    setError(null);
    setFile(f);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files?.[0]);
  };

  const selectedPartner = partners.find((p) => p.id === partner);
  const contractOk = selectedPartner?.contractStatus === 'Signed' && (!selectedPartner.contractEnd || selectedPartner.contractEnd >= new Date().toISOString().slice(0, 10));

  const start = async () => {
    if (!file || !partner) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('partner_id', partner);
      if (profileId) fd.append('profile_id', profileId);
      fd.append('notify_partner', notifyPartner ? 'true' : 'false');
      fd.append('file', file, file.name);
      onStarted(await importsApi.upload(fd));
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  const lastForPartner = recent.find((v) => v.partnerId === partner && v.status === 'Published');
  const drafts = recent.filter((v) => v.status === 'Draft');  // черновики всех поставщиков — видны сразу при входе из меню
  const history = recent.filter((v) => v.status !== 'Draft').slice(0, 15);

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="flex flex-col gap-4 xl:col-span-2">
        {drafts.length > 0 && (
          <div className="rounded-lg border border-accent-200 bg-accent-50/60 p-5">
            <h2 className="text-sm font-semibold text-foreground-950">{t('impX.draftsAllTitle')}</h2>
            <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('impX.draftsAllSub')}</p>
            <ul className="mt-2 divide-y divide-background-200">
              {drafts.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-3 py-2 text-[12px]">
                  <span className="min-w-0 truncate text-foreground-800">
                    <span className="font-medium text-foreground-950">{v.partnerName}</span> · v{v.version} · {v.fileName} · {t('impX.prevItems', { count: v.itemsCount ?? 0 })} · {new Date(v.createdAt).toLocaleString(locale)}
                    {v.error && <span className="ml-2 text-rose-600">{t('impX.hasErrors')}</span>}
                  </span>
                  <button type="button" onClick={() => onOpenExisting(v)} className="inline-flex h-7 shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2.5 text-[11.5px] font-semibold text-background-50 hover:bg-primary-600">
                    {v.finishedAt ? t('impX.continueReview') : t('impX.continueExtraction')} <i className="ri-arrow-right-line text-[11px]" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-950">{t('impUpload.title')}</h2>
          <p className="mt-0.5 text-[12px] leading-5 text-foreground-500">{t('impUpload.sub')}</p>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-9 text-center transition-colors ${
              dragging ? 'border-primary-400 bg-primary-50' : 'border-background-400 bg-background-100/60'
            }`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-primary-600">
              <i className="ri-upload-cloud-2-line text-xl" />
            </span>
            <p className="mt-2.5 text-[13px] font-medium text-foreground-900">{t('impUpload.dropTitle')}</p>
            <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('impUpload.dropSub')}</p>
            <label className="mt-3 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-[11.5px] font-medium text-foreground-700 hover:bg-background-100">
              {t('impUpload.browse')}
              <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv,image/png,image/jpeg" onChange={(e) => pick(e.target.files?.[0])} />
            </label>
          </div>

          {file && (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 p-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-rose-50 text-rose-600">
                  <i className={`${file.name.toLowerCase().endsWith('.pdf') ? 'ri-file-pdf-2-line' : 'ri-file-list-3-line'} text-[16px]`} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[12px] font-medium text-foreground-900">{file.name}</p>
                  <p className="tnum text-[10.5px] text-foreground-500">{formatSize(file.size)}</p>
                </div>
              </div>
              <button type="button" onClick={() => setFile(null)} aria-label={t('impUpload.remove')} className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-foreground-700">
                <i className="ri-close-line text-[14px]" />
              </button>
            </div>
          )}
          {error && <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">{t(`impX.uploadError.${error}`, { defaultValue: `${t('ui.error')}: ${error}` })}</p>}
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-950">{t('impUpload.lookTitle')}</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              { icon: 'ri-price-tag-3-line', title: t('impUpload.look1Title'), text: t('impUpload.look1Text') },
              { icon: 'ri-stack-line', title: t('impUpload.look2Title'), text: t('impUpload.look2Text') },
              { icon: 'ri-settings-3-line', title: t('impUpload.look3Title'), text: t('impUpload.look3Text') },
              { icon: 'ri-link', title: t('impUpload.look4Title'), text: t('impUpload.look4Text') },
            ].map((b) => (
              <div key={b.title} className="flex gap-2.5 rounded-md border border-background-200 bg-background-100/50 p-2.5">
                <i className={`${b.icon} mt-0.5 text-[14px] text-primary-600`} />
                <div>
                  <p className="text-[11.5px] font-medium text-foreground-900">{b.title}</p>
                  <p className="text-[11px] leading-4 text-foreground-500">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div id="history" className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-950">{t('imp.history')}</h2>
          <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('impX.historySub')}</p>
          {history.length === 0 ? (
            <p className="mt-2 text-[12px] text-foreground-500">{t('impX.historyEmpty')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-background-200">
              {history.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-3 py-2 text-[12px]">
                  <span className="min-w-0 truncate text-foreground-800">
                    <span className="font-medium text-foreground-950">{v.partnerName}</span> · v{v.version} · {v.fileName} · {t('impX.prevItems', { count: v.itemsCount ?? 0 })} · {new Date(v.publishedAt || v.createdAt).toLocaleString(locale)}
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${v.status === 'Published' ? 'bg-primary-50 text-primary-800' : 'bg-background-100 text-foreground-500'}`}>{t(`imp.status.${v.status}`, v.status)}</span>
                  </span>
                  <button type="button" onClick={() => onOpenExisting(v)} className="cursor-pointer whitespace-nowrap text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
                    {t('impX.openVersion')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h2 className="text-sm font-semibold text-foreground-950">{t('impUpload.settingsTitle')}</h2>
          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('impUpload.partner')}</span>
            <select value={partner} onChange={(e) => setPartner(e.target.value)} className="h-9 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400">
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.discount}%
                </option>
              ))}
            </select>
          </label>
          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('impX.profile')}</span>
            <select value={profileId} onChange={(e) => setProfileId(e.target.value)} className="h-9 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2.5 text-[12.5px] outline-none focus:border-primary-400">
              <option value="">{t('impX.profileGeneric')}</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · v{p.version}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[10.5px] leading-4 text-foreground-400">{profileId ? t('impX.profileHint') : t('impX.genericHint')}</span>
          </label>

          <div className="mt-3 rounded-md border border-background-200 bg-background-100/60 p-2.5">
            <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-foreground-400">{t('impUpload.contractStatus')}</p>
            <p className={`mt-1 flex items-center gap-1.5 text-[11.5px] font-medium ${contractOk ? 'text-primary-700' : 'text-rose-700'}`}>
              <i className={`${contractOk ? 'ri-shield-check-line' : 'ri-shield-cross-line'} text-[13px]`} /> {contractOk ? t('impUpload.contractSigned') : t('impX.contractMissing', { status: selectedPartner?.contractStatus ?? '' })}
            </p>
          </div>

          <div className="mt-3 space-y-2.5">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={notifyPartner} onChange={(e) => setNotifyPartner(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-[oklch(var(--primary-500))]" />
              <span className="text-[11.5px] leading-4 text-foreground-700">{t('impUpload.notify')}</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked readOnly className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-[oklch(var(--primary-500))]" />
              <span className="text-[11.5px] leading-4 text-foreground-700">{t('impUpload.merge')}</span>
            </label>
          </div>

          <button
            type="button"
            disabled={!file || !partner || busy}
            onClick={() => void start()}
            className="mt-4 flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-[12.5px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className={`${busy ? 'ri-loader-4-line animate-spin' : 'ri-sparkling-2-line'} text-[14px]`} /> {busy ? t('impX.uploading') : t('impUpload.start')}
          </button>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h2 className="text-sm font-semibold text-foreground-950">{t('impUpload.prevTitle')}</h2>
          {lastForPartner ? (
            <>
              <div className="mt-2 flex items-center justify-between text-[11.5px]">
                <span className="text-foreground-500">{t('impX.prevMeta', { version: lastForPartner.version, date: lastForPartner.publishedAt ? new Date(lastForPartner.publishedAt).toLocaleDateString(locale) : '' })}</span>
                <span className="tnum text-foreground-800">{t('impX.prevItems', { count: lastForPartner.itemsCount ?? 0 })}</span>
              </div>
              <p className="mt-2 text-[11px] leading-4 text-foreground-500">{t('impUpload.prevBody')}</p>
            </>
          ) : (
            <p className="mt-2 text-[11px] leading-4 text-foreground-500">{t('impX.noPrev')}</p>
          )}
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
          <div className="flex items-center gap-1.5">
            <i className="ri-information-line text-[13px] text-foreground-400" />
            <p className="text-[11px] font-semibold text-foreground-700">{t('impUpload.formats')}</p>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {formats.map((f) => (
              <span key={f} className="rounded border border-background-200 bg-background-50 px-1.5 py-0.5 text-[10.5px] text-foreground-600">
                {f}
              </span>
            ))}
          </div>
        </div>

        {selectedPartner && (
          <p className="px-1 text-[10.5px] leading-4 text-foreground-400">
            {selectedPartner.name} · {selectedPartner.city}
          </p>
        )}
      </div>
    </div>
  );
}
